import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, ChevronRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import {
  createPayrollExport,
  getPayrollExportWizardConfig,
  previewPayrollExport,
  type PayrollExportCreatePayload,
  type PayrollExportCreateResponse,
  type PayrollExportDeliveryMethod,
  type PayrollExportPreviewPayload,
  type PayrollExportPreviewResponse,
  type PayrollExportProviderKey,
  type PayrollExportWizardConfigResponse,
} from '@/lib/api/payrollBridge';
import type { PayrollPeriodSummary } from '@/lib/api/timekeeping';
import { cn } from '@/lib/utils';

interface PayrollExportWizardProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  payrollPeriods: PayrollPeriodSummary[];
  canExportPush?: boolean;
  canGlDelivery?: boolean;
}

type WizardStepKey = 'setup' | 'validation' | 'delivery' | 'confirmation';

const WIZARD_STEPS: WizardStepKey[] = ['setup', 'validation', 'delivery', 'confirmation'];

const formatDateRange = (start: string, end: string): string => {
  try {
    const formatter = new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${formatter.format(new Date(start))} – ${formatter.format(new Date(end))}`;
  } catch {
    return `${start} – ${end}`;
  }
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) {
    return '-';
  }
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const formatCurrency = (value: number): string => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value / 100);
  } catch {
    return `$${(value / 100).toFixed(0)}`;
  }
};

const formatHours = (value: number): string => {
  return (Math.round(value * 100) / 100).toFixed(2);
};

export const PayrollExportWizard = ({
  open,
  onOpenChange,
  payrollPeriods,
  canExportPush = false,
  canGlDelivery = false,
}: PayrollExportWizardProps) => {
  const { t } = useTranslation(['common', 'payrollBridge']);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [selectedPayPeriodId, setSelectedPayPeriodId] = useState<string>('');
  const [selectedProviderKey, setSelectedProviderKey] = useState<PayrollExportProviderKey | ''>('');
  const [includeChangePacks, setIncludeChangePacks] = useState<boolean>(true);
  const [selectedDelivery, setSelectedDelivery] = useState<PayrollExportDeliveryMethod[]>([]);
  const [selectedGlFormat, setSelectedGlFormat] = useState<string>('');
  const [emailRecipients, setEmailRecipients] = useState<string>('');
  const [previewData, setPreviewData] =
    useState<PayrollExportPreviewResponse['preview'] | null>(null);
  const [exportResult, setExportResult] =
    useState<PayrollExportCreateResponse | null>(null);

  const payPeriodMap = useMemo(() => {
    return new Map(payrollPeriods.map((period) => [period.id, period]));
  }, [payrollPeriods]);

  const configQuery = useQuery<PayrollExportWizardConfigResponse>({
    queryKey: ['payroll-bridge', 'exports', 'config'],
    queryFn: getPayrollExportWizardConfig,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setSelectedDelivery([]);
      setSelectedGlFormat('');
      setEmailRecipients('');
      setPreviewData(null);
      setExportResult(null);
      return;
    }

    if (!selectedPayPeriodId && payrollPeriods.length > 0) {
      setSelectedPayPeriodId(payrollPeriods[0].id);
    }
  }, [open, payrollPeriods, selectedPayPeriodId]);

  useEffect(() => {
    const providers = configQuery.data?.config.providers ?? [];
    if (providers.length > 0 && !selectedProviderKey) {
      setSelectedProviderKey(providers[0].key);
      setSelectedDelivery(providers[0].defaultDelivery);
    }
  }, [configQuery.data, selectedProviderKey]);

  useEffect(() => {
    if (!previewData) {
      return;
    }
    if (selectedDelivery.length === 0 && previewData.recommendedDelivery.length > 0) {
      setSelectedDelivery(previewData.recommendedDelivery);
    }
  }, [previewData, selectedDelivery.length]);

  const previewMutation = useMutation({
    mutationFn: previewPayrollExport,
    onSuccess: (response) => {
      setPreviewData(response.preview);
      setCurrentStep(1);
      toast({
        title: t('payrollBridge:exports.wizard.validationReady', {
          defaultValue: 'Validation complete',
        }),
        description: t('payrollBridge:exports.wizard.validationReadyHint', {
          defaultValue: 'Review blockers and warnings before exporting.',
        }),
      });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : '';
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description: message || t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
    },
  });

  const createExportMutation = useMutation({
    mutationFn: createPayrollExport,
    onSuccess: (response) => {
      setExportResult(response);
      setCurrentStep(3);
      void queryClient.invalidateQueries({ queryKey: ['payroll-exports'] });

      const status = response.export.status;
      if (status === 'completed') {
        toast({
          title: t('payrollBridge:exports.wizard.confirmationToast', {
            defaultValue: 'Export queued',
          }),
          description: t('payrollBridge:exports.wizard.confirmationToastHint', {
            defaultValue: 'We will notify you when delivery finishes.',
          }),
        });
      } else if (status === 'failed_validation') {
        toast({
          variant: 'destructive',
          title: t('payrollBridge:exports.wizard.validationFailedTitle', {
            defaultValue: 'Export blocked by validation',
          }),
          description: t('payrollBridge:exports.wizard.validationFailedHint', {
            defaultValue: 'Resolve blocking issues and try exporting again.',
          }),
        });
      } else if (status.startsWith('failed')) {
        toast({
          variant: 'destructive',
          title: t('payrollBridge:exports.wizard.deliveryFailedTitle', {
            defaultValue: 'Export delivery failed',
          }),
          description: t('payrollBridge:exports.wizard.deliveryFailedHint', {
            defaultValue: 'Check export history for details and retry when ready.',
          }),
        });
      } else {
        toast({
          title: t('payrollBridge:exports.wizard.confirmationToast', {
            defaultValue: 'Export queued',
          }),
          description: t('payrollBridge:exports.wizard.confirmationToastHint', {
            defaultValue: 'We will notify you once delivery finishes.',
          }),
        });
      }
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : '';
      toast({
        variant: 'destructive',
        title: t('common:status.error', { defaultValue: 'Something went wrong' }),
        description: message || t('common:status.tryAgain', { defaultValue: 'Please try again.' }),
      });
    },
  });

  const providers = configQuery.data?.config.providers ?? [];
  const deliveryMethods = configQuery.data?.config.deliveryMethods ?? [];
  const glFormats = configQuery.data?.config.glFormats ?? [];

  const handleRunValidation = useCallback(() => {
    if (!selectedPayPeriodId || !selectedProviderKey) {
      return;
    }
    const payload: PayrollExportPreviewPayload = {
      payPeriodId: selectedPayPeriodId,
      providerKey: selectedProviderKey,
      includeChangePacks,
    };
    previewMutation.mutate(payload);
  }, [includeChangePacks, previewMutation, selectedPayPeriodId, selectedProviderKey]);

  const toggleDelivery = useCallback(
    (method: PayrollExportDeliveryMethod, enabled: boolean) => {
      setSelectedDelivery((current) => {
        if (enabled) {
          return current.includes(method) ? current : [...current, method];
        }
        return current.filter((item) => item !== method);
      });
    },
    [],
  );

  const handleStartExport = useCallback(() => {
    if (!previewData || selectedDelivery.length === 0) {
      return;
    }

    const payload: PayrollExportCreatePayload = {
      payPeriodId: previewData.payPeriodId,
      providerKey: previewData.providerKey,
      includeChangePacks,
      delivery: selectedDelivery,
      glFormat: selectedGlFormat ? (selectedGlFormat as PayrollExportCreatePayload['glFormat']) : null,
    };

    if (selectedDelivery.includes('email')) {
      const recipients = emailRecipients
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      if (recipients.length > 0) {
        payload.emailRecipients = recipients;
      }
    }

    createExportMutation.mutate(payload);
  }, [
    createExportMutation,
    emailRecipients,
    includeChangePacks,
    previewData,
    selectedDelivery,
    selectedGlFormat,
  ]);

  const resetAndClose = useCallback(() => {
    onOpenChange(false);
    setTimeout(() => {
      setCurrentStep(0);
      setSelectedDelivery([]);
      setSelectedGlFormat('');
      setEmailRecipients('');
      setPreviewData(null);
      setExportResult(null);
    }, 200);
  }, [onOpenChange]);

  const renderStepIndicator = () => (
    <ol className="flex flex-wrap items-center gap-2 text-sm">
      {WIZARD_STEPS.map((step, index) => {
        const isActive = index === currentStep;
        const isCompleted = index < currentStep;
        return (
          <li
            key={step}
            className={cn(
              'flex items-center gap-2 rounded-full border px-3 py-1 transition-colors',
              isActive
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:border-emerald-500/50 dark:bg-emerald-500/15 dark:text-emerald-100'
                : isCompleted
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100'
                  : 'border-border/60 bg-muted/20 text-muted-foreground dark:border-border/40 dark:bg-muted/10 dark:text-muted-foreground',
            )}
          >
            <span className="text-xs font-semibold">{index + 1}</span>
            <span className="text-xs font-medium uppercase tracking-wide">
              {t(`payrollBridge:exports.wizard.steps.${step}`, {
                defaultValue: step.toUpperCase(),
              })}
            </span>
          </li>
        );
      })}
    </ol>
  );

  const renderSetupStep = () => (
    <div className="space-y-6">
      <div className="grid gap-4">
        <Label htmlFor="payroll-export-period">
          {t('payrollBridge:exports.wizard.periodLabel', { defaultValue: 'Pay period' })}
        </Label>
        <Select
          value={selectedPayPeriodId}
          onValueChange={setSelectedPayPeriodId}
          disabled={payrollPeriods.length === 0}
        >
          <SelectTrigger id="payroll-export-period" className="w-full">
            <SelectValue
              placeholder={t('payrollBridge:exports.wizard.periodPlaceholder', {
                defaultValue: 'Select a pay period',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            {payrollPeriods.map((period) => (
              <SelectItem key={period.id} value={period.id}>
                {formatDateRange(period.startDate, period.endDate)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {payrollPeriods.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {t('payrollBridge:exports.wizard.noPeriods', {
              defaultValue: 'Stage a payroll period before generating an export.',
            })}
          </p>
        ) : null}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>
            {t('payrollBridge:exports.wizard.providerLabel', { defaultValue: 'Provider' })}
          </Label>
          {configQuery.isLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              {t('payrollBridge:exports.wizard.providersLoading', {
                defaultValue: 'Loading providers…',
              })}
            </div>
          ) : null}
        </div>
        <div className="grid gap-3 lg:grid-cols-2">
          {providers.map((provider) => {
            const isActive = selectedProviderKey === provider.key;
            return (
              <button
                key={provider.key}
                type="button"
                className={cn(
                  'rounded-lg border p-4 text-left transition-colors',
                  isActive
                    ? 'border-emerald-500 bg-emerald-50 shadow-sm dark:border-emerald-500/50 dark:bg-emerald-500/15'
                    : 'border-border bg-card hover:border-emerald-400 dark:border-border/60 dark:bg-slate-900/60 dark:hover:border-emerald-400',
                )}
                onClick={() => {
                  setSelectedProviderKey(provider.key);
                  setSelectedDelivery(provider.defaultDelivery);
                }}
                aria-pressed={isActive}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{provider.name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{provider.description}</p>
                  </div>
                  <Badge
                    variant="outline"
                    className="border-border/60 text-xs text-muted-foreground dark:border-border/40"
                  >
                    {t('payrollBridge:exports.wizard.mappingVersion', {
                      defaultValue: 'Mapping v{{version}}',
                      version: provider.mappingVersion,
                    })}
                  </Badge>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span>
                    {t('payrollBridge:exports.wizard.fileFormats', {
                      defaultValue: 'Files: {{formats}}',
                      formats: provider.supportedFileFormats.join(', '),
                    })}
                  </span>
                  <span>•</span>
                  <span>
                    {t('payrollBridge:exports.wizard.glFormats', {
                      defaultValue: 'GL: {{formats}}',
                      formats: provider.supportedGlFormats.join(', '),
                    })}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
        {!configQuery.isLoading && providers.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {t('payrollBridge:exports.wizard.noProviders', {
              defaultValue: 'No provider mappings available yet.',
            })}
          </p>
        ) : null}
      </div>

      <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-muted/40 p-4 dark:border-border/60 dark:bg-muted/15">
        <div>
          <p className="text-sm font-medium text-foreground">
            {t('payrollBridge:exports.wizard.changePackLabel', {
              defaultValue: 'Include approved change packs',
            })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('payrollBridge:exports.wizard.changePackHint', {
              defaultValue: 'Adds pending employee updates before exporting.',
            })}
          </p>
        </div>
        <Switch
          checked={includeChangePacks}
          onCheckedChange={(value) => setIncludeChangePacks(Boolean(value))}
          aria-label={t('payrollBridge:exports.wizard.changePackToggle', {
            defaultValue: 'Include change packs',
          })}
        />
      </div>
    </div>
  );

  const renderValidationStep = () => {
    if (!previewData) {
      return (
        <div className="space-y-4 text-sm text-muted-foreground">
          {t('payrollBridge:exports.wizard.noPreview', {
            defaultValue: 'Run a validation to see issues and recommended delivery options.',
          })}
        </div>
      );
    }

    const { summary, issues } = previewData;
    const hasBlocking = summary.blockingCount > 0;

    return (
      <div className="space-y-6">
        <div className="grid gap-3 lg:grid-cols-3">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100">
          <p className="text-xs uppercase tracking-wide">
            {t('payrollBridge:exports.wizard.readyEmployees', {
              defaultValue: 'Ready employees',
            })}
          </p>
          <p className="mt-1 text-2xl font-semibold">{summary.readyEmployees}</p>
          <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-100">
            {t('payrollBridge:exports.wizard.employeesReadyTotal', {
              defaultValue: '{{ready}} of {{total}} employees ready',
              ready: summary.readyEmployees,
              total: summary.employeeCount,
            })}
          </p>
          <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-100">
            {t('payrollBridge:exports.wizard.employeesImpacted', {
              defaultValue: '{{count}} impacted',
              count: summary.employeesImpacted,
            })}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 text-foreground dark:border-slate-800/70 dark:bg-slate-900/60">
            <p className="text-xs uppercase tracking-wide">
              {t('payrollBridge:exports.wizard.summaryTotalsLabel', {
                defaultValue: 'Period totals',
              })}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('payrollBridge:exports.wizard.summaryTotals', {
                defaultValue: '{{regular}} regular hrs • {{overtime}} overtime hrs • {{gross}} estimated gross pay',
                regular: formatHours(summary.totalRegularHours),
                overtime: formatHours(summary.totalOvertimeHours),
                gross: formatCurrency(summary.totalGrossPayCents),
              })}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 text-foreground dark:border-slate-800/70 dark:bg-slate-900/60">
            <p className="text-xs uppercase tracking-wide">
              {t('payrollBridge:exports.wizard.validationSummaryLabel', {
                defaultValue: 'Validation results',
              })}
            </p>
            <p className="mt-1 text-sm font-medium">
              {t('payrollBridge:exports.wizard.validationSummary', {
                defaultValue: '{{blocking}} blocking • {{warnings}} warnings',
                blocking: summary.blockingCount,
                warnings: summary.warningCount,
              })}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('payrollBridge:exports.wizard.rulesEvaluated', {
                defaultValue: '{{count}} rules evaluated',
                count: summary.totalRulesEvaluated,
              })}
            </p>
          </div>
        </div>

        {hasBlocking ? (
          <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-rose-800 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100">
            <AlertCircle className="mt-0.5 h-4 w-4" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium">
                {t('payrollBridge:exports.wizard.blockingPresent', {
                  defaultValue: 'Resolve blocking issues before exporting.',
                })}
              </p>
              <p className="mt-1 text-xs text-rose-700 dark:text-rose-100">
                {t('payrollBridge:exports.wizard.blockingHint', {
                  defaultValue: 'Blocking issues must be cleared or overridden with a comment.',
                })}
              </p>
            </div>
          </div>
        ) : null}

        <div className="space-y-3">
          {issues.length === 0 ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100">
              {t('payrollBridge:exports.wizard.noIssues', {
                defaultValue: 'No blocking issues detected.',
              })}
            </div>
          ) : (
            issues.map((issue) => (
              <div
                key={issue.id}
                className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60"
              >
                <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="secondary"
                        className={cn(
                          'border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
                          issue.severity === 'error'
                            ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100'
                            : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100',
                        )}
                      >
                        {issue.severity === 'error'
                          ? t('payrollBridge:exports.wizard.blockingLabel', {
                              defaultValue: 'Blocking',
                            })
                          : t('payrollBridge:exports.wizard.warningLabel', {
                              defaultValue: 'Warning',
                            })}
                      </Badge>
                      <span className="text-sm font-semibold text-foreground">{issue.title}</span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">{issue.message}</p>
                    {issue.remediation ? (
                      <p className="mt-2 text-xs text-muted-foreground">{issue.remediation}</p>
                    ) : null}
                    {issue.link ? (
                      <Button
                        variant="link"
                        size="sm"
                        className="mt-2 px-0"
                        onClick={() => {
                          if (typeof window === 'undefined' || !issue.link) {
                            return;
                          }
                          try {
                            const targetUrl = new URL(issue.link, window.location.origin);
                            if (targetUrl.origin !== window.location.origin) {
                              console.warn('[payrollBridge] blocked navigation to external issue link', {
                                link: issue.link,
                              });
                              return;
                            }
                            const relativeTarget = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
                            window.location.assign(relativeTarget);
                          } catch (error) {
                            console.warn('[payrollBridge] failed to navigate to issue link', {
                              link: issue.link,
                              error,
                            });
                          }
                        }}
                      >
                        {t('payrollBridge:exports.wizard.openLink', {
                          defaultValue: 'Open related page',
                        })}
                        <ChevronRight className="ml-1 h-3 w-3" aria-hidden="true" />
                      </Button>
                    ) : null}
                  </div>
                  {issue.affectedCount ? (
                    <span className="text-xs text-muted-foreground">
                      {t('payrollBridge:exports.wizard.issueAffected', {
                        defaultValue: '{{count}} affected',
                        count: issue.affectedCount,
                      })}
                    </span>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderDeliveryStep = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>
          {t('payrollBridge:exports.wizard.deliveryLabel', {
            defaultValue: 'Delivery methods',
          })}
        </Label>
        <p className="text-xs text-muted-foreground">
          {t('payrollBridge:exports.wizard.deliveryHint', {
            defaultValue: 'Choose how the export should be delivered.',
          })}
        </p>
      </div>
      <div className="space-y-3">
        {deliveryMethods.map((method) => {
          const enabled = selectedDelivery.includes(method.key);
          const isPushMethod = method.key === 'sftp' || method.key === 'api';
          const disabled = isPushMethod && !canExportPush;
          return (
            <label
              key={method.key}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-card p-4 shadow-sm transition-colors hover:border-emerald-400 dark:border-border/60 dark:bg-slate-900/60 dark:hover:border-emerald-400',
                enabled ? 'border-emerald-400 bg-emerald-50/70 dark:border-emerald-400/70 dark:bg-emerald-500/15' : '',
                disabled ? 'opacity-60 cursor-not-allowed hover:border-border dark:hover:border-border' : '',
              )}
            >
              <Checkbox
                checked={enabled && !disabled}
                onCheckedChange={(value) => {
                  if (disabled) {
                    return;
                  }
                  toggleDelivery(method.key, Boolean(value));
                }}
                className="mt-1"
                disabled={disabled}
              />
              <div className="flex flex-1 flex-col gap-1 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground">{method.label}</span>
                  {method.requiresPayrollSync ? (
                    <Badge
                      variant="outline"
                      className="border-sky-200 bg-sky-50 text-[11px] uppercase tracking-wide text-sky-700 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-100"
                    >
                      {t('payrollBridge:exports.wizard.deliveryRequiresSync', {
                        defaultValue: 'Requires Payroll Sync',
                      })}
                    </Badge>
                  ) : null}
                </div>
                <span className="text-xs text-muted-foreground">
                  {method.description}
                  {disabled
                    ? ` ${t('payrollBridge:exports.wizard.deliveryEntitlementHint', {
                        defaultValue: 'Upgrade to Payroll Sync to enable automated delivery.',
                      })}`
                    : ''}
                </span>
              </div>
            </label>
          );
        })}
      </div>

      {selectedDelivery.includes('email') ? (
        <div className="space-y-2">
          <Label htmlFor="payroll-export-emails">
            {t('payrollBridge:exports.wizard.emailLabel', {
              defaultValue: 'Notification emails',
            })}
          </Label>
          <Input
            id="payroll-export-emails"
            placeholder={t('payrollBridge:exports.wizard.emailPlaceholder', {
              defaultValue: 'finance@example.com, payroll@example.com',
            })}
            value={emailRecipients}
            onChange={(event) => setEmailRecipients(event.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            {t('payrollBridge:exports.wizard.emailsInfo', {
              defaultValue: 'Separate multiple emails with commas.',
            })}
          </p>
        </div>
      ) : null}

      <div className="grid gap-2">
        <Label htmlFor="payroll-export-gl">
          {t('payrollBridge:exports.wizard.glLabel', { defaultValue: 'GL file format' })}
          <span className="ml-2 text-xs text-muted-foreground">
            {t('payrollBridge:exports.wizard.glOptional', { defaultValue: 'Optional' })}
          </span>
        </Label>
        <Select
          value={selectedGlFormat}
          onValueChange={setSelectedGlFormat}
          disabled={!canGlDelivery}
        >
          <SelectTrigger id="payroll-export-gl" className="w-full">
            <SelectValue
              placeholder={t('payrollBridge:exports.wizard.glPlaceholder', {
                defaultValue: 'Select GL format (optional)',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">
              {t('payrollBridge:exports.wizard.glNone', { defaultValue: 'No GL export' })}
            </SelectItem>
            {glFormats.map((format) => (
              <SelectItem key={format.key} value={format.key}>
                {format.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {canGlDelivery
            ? t('payrollBridge:exports.wizard.glHint', {
                defaultValue: 'Include a GL file if accounting needs to import journal entries.',
              })
            : t('payrollBridge:exports.wizard.glEntitlementHint', {
                defaultValue: 'GL export is available for Payroll Sync or higher plans.',
              })}
        </p>
      </div>
    </div>
  );

  const renderConfirmationStep = () => {
    if (!exportResult) {
      return null;
    }

    const { export: exportData, files, validation } = exportResult;
    const period = payPeriodMap.get(exportData.payPeriodId ?? '');
    const provider = providers.find((item) => item.key === exportData.provider);
    const deliveryText =
      exportData.deliveryMethods.length > 0
        ? exportData.deliveryMethods.join(', ')
        : t('payrollBridge:exports.wizard.deliveryPending', {
            defaultValue: 'Delivery pending',
          });

    const status = exportData.status;
    const isCompleted = status === 'completed';
    const isFailure = status.startsWith('failed');
    const statusTitleKey = isCompleted
      ? 'payrollBridge:exports.wizard.summaryCompletedTitle'
      : isFailure
        ? 'payrollBridge:exports.wizard.summaryFailedTitle'
        : 'payrollBridge:exports.wizard.summaryInProgressTitle';
    const statusSubtitleKey = isCompleted
      ? 'payrollBridge:exports.wizard.summaryCompletedSubtitle'
      : isFailure
        ? 'payrollBridge:exports.wizard.summaryFailedSubtitle'
        : 'payrollBridge:exports.wizard.summaryInProgressSubtitle';

    const statusBadgeClass = cn(
      'border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
      isCompleted
        ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100'
        : isFailure
          ? 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100'
          : 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-100',
    );

    const statusLabel = t(`payrollBridge:exports.status.${status}`, {
      defaultValue: status.replace(/_/g, ' '),
    });

    const summary = validation.summary;
    const issues = validation.issues;

    return (
      <div className="space-y-6 text-sm text-muted-foreground">
        <div
          className={cn(
            'flex items-start gap-3 rounded-lg border p-4',
            isCompleted
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100'
              : isFailure
                ? 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100'
                : 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-100',
          )}
        >
          <CheckCircle2 className="mt-0.5 h-5 w-5" aria-hidden="true" />
          <div>
            <h3 className="text-base font-semibold">{t(statusTitleKey)}</h3>
            <p className="mt-1 text-sm">
              {t(statusSubtitleKey)}
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60">
          <dl className="grid gap-3">
            <div className="flex items-center justify-between">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('payrollBridge:exports.wizard.providerLabel', { defaultValue: 'Provider' })}
              </dt>
              <dd className="text-sm font-medium text-foreground">
                {provider?.name ?? exportData.provider.toUpperCase()}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('payrollBridge:exports.wizard.periodLabel', { defaultValue: 'Pay period' })}
              </dt>
              <dd className="text-sm font-medium text-foreground">
                {period
                  ? formatDateRange(period.startDate, period.endDate)
                  : exportData.payPeriodId ?? '-'}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('payrollBridge:exports.wizard.statusLabel', { defaultValue: 'Status' })}
              </dt>
              <dd>
                <span className={statusBadgeClass}>{statusLabel}</span>
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('payrollBridge:exports.wizard.deliveryLabel', {
                  defaultValue: 'Delivery methods',
                })}
              </dt>
              <dd className="text-sm font-medium text-foreground">{deliveryText}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('payrollBridge:exports.wizard.glLabel', { defaultValue: 'GL file format' })}
              </dt>
              <dd className="text-sm font-medium text-foreground">
                {exportData.glFormat ??
                  t('payrollBridge:exports.wizard.glNone', { defaultValue: 'No GL export' })}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('payrollBridge:exports.wizard.requestedAt', { defaultValue: 'Requested' })}
              </dt>
              <dd className="text-sm font-medium text-foreground">
                {formatDateTime(exportData.requestedAt)}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('payrollBridge:exports.wizard.completedAt', { defaultValue: 'Completed' })}
              </dt>
              <dd className="text-sm font-medium text-foreground">
                {formatDateTime(exportData.completedAt)}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('payrollBridge:exports.wizard.exportId', { defaultValue: 'Export ID' })}
              </dt>
              <dd className="text-sm font-medium text-foreground">{exportData.id}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-foreground">
              {t('payrollBridge:exports.wizard.validationSummaryTitle', {
                defaultValue: 'Validation summary',
              })}
            </h4>
            <span className="text-xs text-muted-foreground">
              {t('payrollBridge:exports.wizard.validationCounts', {
                defaultValue: '{{blocking}} blocking • {{warnings}} warnings',
                blocking: summary.blockingCount,
                warnings: summary.warningCount,
              })}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('payrollBridge:exports.wizard.validationEmployees', {
              defaultValue: '{{ready}} of {{total}} employees ready',
              ready: summary.readyEmployees,
              total: summary.employeeCount,
            })}
          </p>
          {issues.length === 0 ? (
            <p className="mt-3 text-sm text-emerald-700 dark:text-emerald-100">
              {t('payrollBridge:exports.wizard.noIssues', {
                defaultValue: 'No blocking issues detected.',
              })}
            </p>
          ) : (
            <div className="mt-4 space-y-3">
              {issues.map((issue) => (
                <div
                  key={`${issue.id}-${issue.ruleId}`}
                  className="rounded-lg border border-border bg-muted/40 p-3 dark:border-border/60 dark:bg-muted/15"
                >
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className={cn(
                        'border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
                        issue.severity === 'error'
                          ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100'
                          : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100',
                      )}
                    >
                      {issue.severity === 'error'
                        ? t('payrollBridge:exports.wizard.blockingLabel', {
                            defaultValue: 'Blocking',
                          })
                        : t('payrollBridge:exports.wizard.warningLabel', {
                            defaultValue: 'Warning',
                          })}
                    </Badge>
                    <span className="text-sm font-semibold text-foreground">{issue.title}</span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{issue.message}</p>
                  {issue.remediation ? (
                    <p className="mt-1 text-xs text-muted-foreground">{issue.remediation}</p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-slate-800/70 dark:bg-slate-900/60">
          <h4 className="text-sm font-semibold text-foreground">
            {t('payrollBridge:exports.wizard.generatedFilesTitle', {
              defaultValue: 'Generated files',
            })}
          </h4>
          {files.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">
              {t('payrollBridge:exports.wizard.noFiles', {
                defaultValue: 'No files were generated for this run.',
              })}
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {files.map((file) => (
                <li
                  key={file.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2 dark:border-border/60 dark:bg-muted/15"
                >
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {file.filename ?? `${file.fileType}.${(file.metadata?.format as string) ?? 'csv'}`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('payrollBridge:exports.wizard.fileMeta', {
                        defaultValue: '{{type}} • {{size}}',
                        type: file.fileType,
                        size: file.sizeBytes ? `${(file.sizeBytes / 1024).toFixed(1)} KB` : '-',
                      })}
                    </p>
                  </div>
                  <Button
                    asChild
                    variant="link"
                    size="sm"
                    className="px-0"
                  >
                    <a
                      href={`/api/payroll_bridge/exports/${exportData.id}/files/${file.id}/download`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('payrollBridge:exports.wizard.downloadFile', { defaultValue: 'Download' })}
                    </a>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderSetupStep();
      case 1:
        return renderValidationStep();
      case 2:
        return renderDeliveryStep();
      case 3:
        return renderConfirmationStep();
      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (currentStep === 3) {
      return (
        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <Button variant="outline" onClick={() => setCurrentStep(0)}>
            {t('payrollBridge:exports.wizard.startAnother', {
              defaultValue: 'Start another export',
            })}
          </Button>
          <Button onClick={resetAndClose}>
            {t('payrollBridge:exports.wizard.close', { defaultValue: 'Close' })}
          </Button>
        </DialogFooter>
      );
    }

    if (currentStep === 2) {
      return (
        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <Button variant="ghost" onClick={() => setCurrentStep(currentStep - 1)}>
            {t('payrollBridge:exports.wizard.goBack', { defaultValue: 'Back' })}
          </Button>
          <Button
            onClick={handleStartExport}
            disabled={selectedDelivery.length === 0 || createExportMutation.isLoading}
          >
            {createExportMutation.isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {t('payrollBridge:exports.wizard.startingExport', { defaultValue: 'Starting…' })}
              </span>
            ) : (
              t('payrollBridge:exports.wizard.startExport', { defaultValue: 'Start export' })
            )}
          </Button>
        </DialogFooter>
      );
    }

    if (currentStep === 1) {
      const hasBlocking = previewData?.summary.blockingCount ?? 0;
      return (
        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button variant="ghost" onClick={() => setCurrentStep(currentStep - 1)}>
              {t('payrollBridge:exports.wizard.goBack', { defaultValue: 'Back' })}
            </Button>
            <Button
              variant="secondary"
              onClick={handleRunValidation}
              disabled={previewMutation.isLoading}
            >
              {previewMutation.isLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  {t('payrollBridge:exports.wizard.validationPending', {
                    defaultValue: 'Running…',
                  })}
                </span>
              ) : (
                t('payrollBridge:exports.wizard.validationRefresh', {
                  defaultValue: 'Re-run validation',
                })
              )}
            </Button>
          </div>
          <Button onClick={() => setCurrentStep(currentStep + 1)} disabled={hasBlocking > 0}>
            {t('payrollBridge:exports.wizard.continue', { defaultValue: 'Continue' })}
          </Button>
        </DialogFooter>
      );
    }

    return (
      <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
        <Button variant="ghost" onClick={resetAndClose}>
          {t('common:actions.cancel', { defaultValue: 'Cancel' })}
        </Button>
        <Button
          onClick={handleRunValidation}
          disabled={
            !selectedPayPeriodId ||
            !selectedProviderKey ||
            previewMutation.isLoading ||
            configQuery.isLoading
          }
        >
          {previewMutation.isLoading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t('payrollBridge:exports.wizard.validationPending', {
                defaultValue: 'Running…',
              })}
            </span>
          ) : (
            t('payrollBridge:exports.wizard.runValidation', { defaultValue: 'Run validation' })
          )}
        </Button>
      </DialogFooter>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {t('payrollBridge:exports.wizard.title', {
              defaultValue: 'Payroll export wizard',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('payrollBridge:exports.wizard.subtitle', {
              defaultValue: 'Validate payroll data and deliver provider-ready files.',
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {renderStepIndicator()}
          {renderStepContent()}
        </div>
        {renderFooter()}
      </DialogContent>
    </Dialog>
  );
};

export default PayrollExportWizard;
